import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { MaterialReactTable } from 'material-react-table';
import axios from 'axios';
import { 
  FiSearch, 
  FiSettings, 
  FiPlus, 
  FiChevronDown,
  FiMail,
  FiMessageSquare,
  FiDownload,
  FiX,
  FiCheck,
  FiClock,
  FiPause,
  FiSquare,
  FiAlertTriangle,
  FiFileText,
  FiLock,
  FiEye,
  FiFilter,
  FiInfo,
  FiUser,
  FiCalendar,
  FiTag
} from 'react-icons/fi';
import { 
  getDetailTreatmentsByPatient,
  getDetailTreatmentsSummary,
  updateTreatmentConsent,
  updateTreatmentProgress,
  deleteDetailTreatment,
  createDetailTreatmentWithDoctor,
  validateTreatmentData, 
  validateObjectId  // 🔥 NUEVO: Usar la función con doctor
} from '../../../PatientTreatmentDetails/services/detailTreatmentService';
import { API_BASE_URL, API_DOCTOR_LIST } from '../../../../helpers/urlHelper';

// 🔥 CAMBIO 1: Añadido componentName como prop
const DetailsTreatments = ({ patientId, onCountUpdate, componentName }) => {
  const [treatments, setTreatments] = useState([]);
  const [filteredTreatments, setFilteredTreatments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [rowSelection, setRowSelection] = useState({});
  const [globalFilter, setGlobalFilter] = useState('');
  const [alertInfo, setAlertInfo] = useState(null);
  const [showOffcanvas, setShowOffcanvas] = useState(false);
  const [showFilterOffcanvas, setShowFilterOffcanvas] = useState(false);
  const [showViewOffcanvas, setShowViewOffcanvas] = useState(false);
  const [selectedTreatment, setSelectedTreatment] = useState(null);
  const [showMostRecentDropdown, setShowMostRecentDropdown] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [availableTreatments, setAvailableTreatments] = useState([]);
  const [loadingTreatments, setLoadingTreatments] = useState(false);
  
  // 🔥 NUEVO: Estados para doctores
  const [doctors, setDoctors] = useState([]);
  const [loadingDoctors, setLoadingDoctors] = useState(false);
  
  // Filter state
  const [filters, setFilters] = useState({
    treatmentType: '',
    lastUpdate: '',
    startDate: '',
    endDate: '',
    status: 'All',
    consentStatus: 'All'
  });

  // Form state for new treatment
  const [newTreatment, setNewTreatment] = useState({
    patient_id: patientId,
    doctor_id: '', // 🔥 NUEVO: Campo para doctor
    treatment_info: {
      treatment_template_id: '',
      name: '',
      protocol_code: '',
      status: 'active',
      start_date: '',
      estimated_end_date: ''
    },
    consent_status: {
      is_required: true,
      is_signed: false,
      pending_signature: true
    },
    phases_summary: [],
    active_alerts: []
  });

  // 🔥 NUEVO: Función para obtener doctores
  const fetchDoctors = useCallback(async () => {
    setLoadingDoctors(true);
    try {
      const doctorsResponse = await fetch(`${API_DOCTOR_LIST}`);
      if (!doctorsResponse.ok) {
        throw new Error('Failed to fetch doctors');
      }
      const doctorsData = await doctorsResponse.json();
      console.log('Raw doctors response:', doctorsData);
      
      const doctorsArray = doctorsData && doctorsData.data ? doctorsData.data : [];
      console.log('Processed doctors array:', doctorsArray);
      setDoctors(doctorsArray);
    } catch (err) {
      console.error('Error fetching doctors:', err);
      setDoctors([]);
    } finally {
      setLoadingDoctors(false);
    }
  }, []);

  // 🔥 NUEVO: Función para manejar selección de doctor
  const handleDoctorSelection = useCallback((doctorId) => {
    console.log('Doctor seleccionado:', doctorId);
    console.log('Lista de doctores disponibles:', doctors);

    const selectedDoctor = doctors.find(d => d._id === doctorId);
    console.log('Doctor encontrado:', selectedDoctor);

    if (selectedDoctor) {
      setNewTreatment(prev => {
        const updated = { 
          ...prev, 
          doctor_id: selectedDoctor._id,
          // También podemos guardar información adicional del doctor
          doctor_info: {
            doctor_name: `${selectedDoctor.name} ${selectedDoctor.surname}`,
            doctor_email: selectedDoctor.email || '',
            specialty: selectedDoctor.specialty || '',
            license_number: selectedDoctor.license_number || ''
          }
        };
        console.log('Estado actualizado newTreatment.doctor_id:', updated.doctor_id);
        return updated;
      });
    } else if (doctorId === '') {
      // Caso cuando se deselecciona el doctor
      setNewTreatment(prev => ({ 
        ...prev, 
        doctor_id: '',
        doctor_info: undefined
      }));
      console.log('Doctor deseleccionado, doctor_id limpiado');
    } else {
      console.warn('Doctor no encontrado para ID:', doctorId);
    }
  }, [doctors]);

  // 🔥 CAMBIO 2: Memoizar funciones con useCallback para evitar recreaciones
  const loadTreatments = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getDetailTreatmentsByPatient(patientId);
      console.log('Raw API response:', response);
      
      // Normalizar los datos dependiendo de la estructura de respuesta
      let treatmentsData = [];
      
      if (response.data) {
        treatmentsData = Array.isArray(response.data) ? response.data : [response.data];
      } else if (Array.isArray(response)) {
        treatmentsData = response;
      } else if (response.treatments) {
        treatmentsData = Array.isArray(response.treatments) ? response.treatments : [response.treatments];
      }

      // Procesar y normalizar cada tratamiento
      const normalizedTreatments = treatmentsData.map(treatment => ({
        // ID del tratamiento - puede venir en diferentes campos
        treatment_id: treatment.treatment_id || treatment._id || treatment.id,
        
        // Información del tratamiento
        treatment_info: {
          name: treatment.treatment_info?.name || 
                treatment.name || 
                treatment.treatment_name || 
                'Unnamed Treatment',
          protocol_code: treatment.treatment_info?.protocol_code || 
                        treatment.protocol_code || 
                        treatment.code || 
                        '-',
          status: treatment.treatment_info?.status || 
                 treatment.status || 
                 'active',
          start_date: treatment.treatment_info?.start_date || 
                     treatment.start_date || 
                     treatment.created_date ||
                     treatment.createdAt,
          estimated_end_date: treatment.treatment_info?.estimated_end_date || 
                             treatment.estimated_end_date || 
                             treatment.end_date,
          last_updated: treatment.treatment_info?.last_updated ||
                       treatment.last_updated ||
                       treatment.updatedAt ||
                       treatment.modified_date,
          treatment_template_id: treatment.treatment_info?.treatment_template_id || 
                               treatment.treatment_template_id ||
                               treatment.template_id,
          description: treatment.treatment_info?.description || 
                      treatment.description || 
                      'No description available'
        },
        
        // 🔥 NUEVO: Información del doctor
        doctor_info: {
          doctor_name: treatment.doctor_info?.doctor_name || 'Not assigned',
          doctor_email: treatment.doctor_info?.doctor_email || '',
          specialty: treatment.doctor_info?.specialty || '',
          license_number: treatment.doctor_info?.license_number || ''
        },
        
        // Estado del consentimiento
        consent_status: {
          is_required: treatment.consent_status?.is_required ?? 
                      treatment.consent_required ?? 
                      true,
          is_signed: treatment.consent_status?.is_signed ?? 
                    treatment.consent_signed ?? 
                    treatment.consent_status === 'signed' ?? 
                    false,
          pending_signature: treatment.consent_status?.pending_signature ?? 
                           treatment.consent_pending ?? 
                           true,
          signed_date: treatment.consent_status?.signed_date ||
                      treatment.consent_signed_date,
          document_url: treatment.consent_status?.document_url ||
                       treatment.consent_document_url
        },
        
        // Alertas activas
        active_alerts: treatment.active_alerts || 
                      treatment.alerts || 
                      [],
        
        // Fases del tratamiento
        phases_summary: treatment.phases_summary || 
                       treatment.phases || 
                       [],

        // Información del paciente
        patient_id: treatment.patient_id || patientId,
        doctor_id: treatment.doctor_id || '', // 🔥 NUEVO
        
        // Metadatos adicionales
        created_date: treatment.created_date || treatment.createdAt,
        updated_date: treatment.updated_date || treatment.updatedAt || treatment.last_updated,
        created_by: treatment.created_by || treatment.author,
        
        // Información adicional
        specialty: treatment.specialty || treatment.specialty_name,
        duration_days: treatment.duration_days || treatment.estimated_duration_days,
        progress_percentage: treatment.progress_percentage || 0,
        
        // Datos originales para debugging
        _original: treatment
      }));

      console.log('Normalized treatments:', normalizedTreatments);
      setTreatments(normalizedTreatments);
    } catch (error) {
      console.error('Error loading treatments:', error);
      setTreatments([]);
    } finally {
      setIsLoading(false);
    }
  }, [patientId]); // 🔥 CAMBIO 3: Solo depende de patientId

  const loadTreatmentsSummary = useCallback(async () => {
    try {
      const response = await getDetailTreatmentsSummary(patientId);
      console.log('Summary response:', response);
      
      let alertsData = [];
      
      // Normalizar alertas de diferentes estructuras de respuesta
      if (response.data?.active_alerts) {
        alertsData = response.data.active_alerts;
      } else if (response.active_alerts) {
        alertsData = response.active_alerts;
      } else if (response.alerts) {
        alertsData = response.alerts;
      }
      
      if (alertsData && alertsData.length > 0) {
        setAlertInfo(alertsData[0]);
      } else {
        setAlertInfo(null);
      }
    } catch (error) {
      console.error('Error loading treatments summary:', error);
      setAlertInfo(null);
    }
  }, [patientId]); // 🔥 CAMBIO 4: Solo depende de patientId

  // Load treatments data
  useEffect(() => {
    if (patientId) {
      loadTreatments();
      loadTreatmentsSummary();
      setNewTreatment(prev => ({
        ...prev,
        patient_id: patientId
      }));
    }
  }, [patientId, loadTreatments, loadTreatmentsSummary]); // 🔥 CAMBIO 5: Incluye las funciones memoizadas

  // 🔥 CAMBIO 6: CRÍTICO - Cambiar la lógica del contador para evitar loops infinitos
  useEffect(() => {
    if (onCountUpdate && typeof onCountUpdate === 'function' && componentName) {
      // Solo actualizar si el contador realmente cambió
      const currentCount = treatments.length;
      onCountUpdate(componentName, currentCount);
    }
  }, [treatments.length, onCountUpdate, componentName]); // 🔥 Solo depende de la longitud, no del array completo

  // 🔥 CAMBIO 7: Memoizar la función applyFilters
  const applyFilters = useCallback(() => {
    let filtered = [...treatments];

    // Global search filter
    if (globalFilter) {
      filtered = filtered.filter(treatment => 
        treatment.treatment_info.name.toLowerCase().includes(globalFilter.toLowerCase()) ||
        treatment.treatment_info.protocol_code.toLowerCase().includes(globalFilter.toLowerCase())
      );
    }

    // Status filter
    if (filters.status !== 'All') {
      const statusValue = filters.status.toLowerCase().replace(' ', '_');
      filtered = filtered.filter(treatment => 
        treatment.treatment_info.status.toLowerCase() === statusValue
      );
    }

    // Consent status filter
    if (filters.consentStatus !== 'All') {
      if (filters.consentStatus === 'Signed') {
        filtered = filtered.filter(treatment => treatment.consent_status.is_signed);
      } else if (filters.consentStatus === 'Pending') {
        filtered = filtered.filter(treatment => 
          treatment.consent_status.is_required && !treatment.consent_status.is_signed
        );
      } else if (filters.consentStatus === 'Not Required') {
        filtered = filtered.filter(treatment => !treatment.consent_status.is_required);
      }
    }

    // Date filters
    if (filters.startDate) {
      filtered = filtered.filter(treatment => {
        const startDate = treatment.treatment_info.start_date;
        return startDate && new Date(startDate) >= new Date(filters.startDate);
      });
    }

    if (filters.endDate) {
      filtered = filtered.filter(treatment => {
        const startDate = treatment.treatment_info.start_date;
        return startDate && new Date(startDate) <= new Date(filters.endDate);
      });
    }

    // Last update filter
    if (filters.lastUpdate) {
      filtered = filtered.filter(treatment => {
        const lastUpdated = treatment.treatment_info.last_updated || 
                           treatment.updated_date || 
                           treatment.treatment_info.start_date ||
                           treatment.created_date;
        
        if (!lastUpdated) return false;
        
        try {
          const updateDate = new Date(lastUpdated).toISOString().split('T')[0];
          return updateDate === filters.lastUpdate;
        } catch {
          return false;
        }
      });
    }

    setFilteredTreatments(filtered);
  }, [treatments, filters, globalFilter]); // 🔥 CAMBIO 8: Dependencias memoizadas

  // Apply filters whenever treatments or filters change
  useEffect(() => {
    applyFilters();
  }, [applyFilters]); // 🔥 CAMBIO 9: Solo depende de la función memoizada

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown')) {
        setShowMostRecentDropdown(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Prevent body scroll when offcanvas are open
  useEffect(() => {
    if (showFilterOffcanvas || showOffcanvas || showViewOffcanvas) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showFilterOffcanvas, showOffcanvas, showViewOffcanvas]);

  // 🔥 CAMBIO 10: Memoizar funciones de manejo de eventos
  const handleGlobalFilterChange = useCallback((filter) => {
    setGlobalFilter(filter);
  }, []);

  const handleViewTreatment = useCallback((treatmentId) => {
    const treatment = treatments.find(t => t.treatment_id === treatmentId);
    if (treatment) {
      setSelectedTreatment(treatment);
      setShowViewOffcanvas(true);
    }
  }, [treatments]);

  const handleConsentAction = useCallback(async (treatmentId) => {
    try {
      await updateTreatmentConsent(treatmentId, { is_signed: true });
      await loadTreatments();
      await loadTreatmentsSummary();
    } catch (error) {
      console.error('Error updating consent:', error);
    }
  }, [loadTreatments, loadTreatmentsSummary]);

  const handleAddNew = useCallback(() => {
    setShowOffcanvas(true);
    loadAvailableTreatments();
    fetchDoctors(); // 🔥 NUEVO: Cargar doctores al abrir el modal
  }, [fetchDoctors]);

  const handleMostRecentAction = useCallback((action) => {
    setShowMostRecentDropdown(false);
    switch (action) {
      case 'sendMail':
        console.log('Send mail action');
        break;
      case 'sendMessage':
        console.log('Send message action');
        break;
      case 'downloadReport':
        console.log('Download report action');
        break;
      default:
        break;
    }
  }, []);

  const handleFilterChange = useCallback((filterKey, value) => {
    setFilters(prev => ({
      ...prev,
      [filterKey]: value
    }));
  }, []);

  const handleApplyFilters = useCallback(() => {
    setShowFilterOffcanvas(false);
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters({
      treatmentType: '',
      lastUpdate: '',
      startDate: '',
      endDate: '',
      status: 'All',
      consentStatus: 'All'
    });
  }, []);

  const loadAvailableTreatments = useCallback(async () => {
    setLoadingTreatments(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/treatmentManagement`
      );
      
      const mappedTreatments = response.data.map(treatment => ({
        id: treatment._id || treatment.treatment_id,
        name: treatment.template.name,
        protocol_code: treatment.template.protocol_code,
        category: treatment.template.specialty_id || 'General',
        estimated_duration_days: treatment.phases && treatment.phases.length > 0 
          ? treatment.phases.reduce((total, phase) => total + (phase.estimated_duration_days || 0), 0)
          : 30,
        version: treatment.template.version,
        purpose: treatment.template.purpose,
        status: treatment.template.status,
        authors: treatment.template.authors,
        keywords: treatment.template.keywords,
        description: treatment.template.description || 'Sin descripción disponible',
        phases_count: treatment.phases ? treatment.phases.length : 0,
        phases: treatment.phases || [],
        specialty_name: treatment.template.specialty_name || 'General',
        created_date: treatment.template.created_date,
        last_modified: treatment.template.last_modified
      }));
      
      setAvailableTreatments(mappedTreatments);
      console.log('Treatments loaded successfully:', mappedTreatments);
    } catch (error) {
      console.error('Error loading available treatments:', error);
      setAvailableTreatments([]);
    } finally {
      setLoadingTreatments(false);
    }
  }, []);

  const handleCloseOffcanvas = useCallback(() => {
    setShowOffcanvas(false);
    setNewTreatment({
      patient_id: patientId,
      doctor_id: '', // 🔥 NUEVO: Resetear doctor_id
      treatment_info: {
        treatment_template_id: '',
        name: '',
        protocol_code: '',
        status: 'active',
        start_date: '',
        estimated_end_date: ''
      },
      consent_status: {
        is_required: true,
        is_signed: false,
        pending_signature: true
      },
      phases_summary: [],
      active_alerts: []
    });
  }, [patientId]);

  const handleFormChange = useCallback((field, value, section = null) => {
    if (section) {
      setNewTreatment(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value
        }
      }));
    } else {
      setNewTreatment(prev => ({
        ...prev,
        [field]: value
      }));
    }
  }, []);

  const handleTreatmentSelection = useCallback((treatmentId) => {
    const selectedTreatment = availableTreatments.find(t => t.id === treatmentId);
    if (selectedTreatment) {
      const startDate = newTreatment.treatment_info.start_date;
      let estimatedEndDate = '';
      
      if (startDate && selectedTreatment.estimated_duration_days) {
        const start = new Date(startDate);
        const end = new Date(start.getTime() + (selectedTreatment.estimated_duration_days * 24 * 60 * 60 * 1000));
        estimatedEndDate = end.toISOString().split('T')[0];
      }

      setNewTreatment(prev => ({
        ...prev,
        treatment_info: {
          ...prev.treatment_info,
          treatment_template_id: treatmentId,
          name: selectedTreatment.name,
          protocol_code: selectedTreatment.protocol_code,
          estimated_end_date: estimatedEndDate
        }
      }));
    }
  }, [availableTreatments, newTreatment.treatment_info.start_date]);

// 🔥 FUNCIÓN MEJORADA con pre-validación
const handleSubmitNewTreatment = useCallback(async (e) => {
  e.preventDefault();
  setIsSubmitting(true);
  
  try {
    console.log('🔍 Form validation starting...');
    console.log('📋 Current form state:', {
      doctor_id: newTreatment.doctor_id,
      treatment_template_id: newTreatment.treatment_info.treatment_template_id,
      patient_id: patientId,
      start_date: newTreatment.treatment_info.start_date
    });

    // 🔍 VALIDACIONES BÁSICAS MEJORADAS
    if (!patientId) {
      throw new Error('Patient ID is missing from component props');
    }

    if (!newTreatment.doctor_id) {
      throw new Error('Please select a doctor for this treatment');
    }

    if (!newTreatment.treatment_info.treatment_template_id) {
      throw new Error('Please select a treatment template');
    }

    if (!newTreatment.treatment_info.start_date) {
      throw new Error('Please specify a start date for the treatment');
    }

    // 🔍 PRE-VALIDAR OBJECTIDS
    console.log('🔍 Validating ObjectId formats...');
    
    const patientIdError = validateObjectId(patientId, 'Patient ID');
    if (patientIdError) throw new Error(patientIdError);

    const doctorIdError = validateObjectId(newTreatment.doctor_id, 'Doctor ID');
    if (doctorIdError) throw new Error(doctorIdError);

    const templateIdError = validateObjectId(newTreatment.treatment_info.treatment_template_id, 'Treatment Template ID');
    if (templateIdError) throw new Error(templateIdError);

    console.log('✅ All ObjectId formats are valid');

    // 🏗️ CONSTRUIR DATOS DEL TRATAMIENTO
    const treatmentData = {
      treatmentManagementId: newTreatment.treatment_info.treatment_template_id,
      patient_id: patientId,
      doctor_id: newTreatment.doctor_id,
      
      doctor_info: {
        doctor_name: newTreatment.doctor_info?.doctor_name || "Doctor Name Pending",
        doctor_email: newTreatment.doctor_info?.doctor_email || "",
        specialty: newTreatment.doctor_info?.specialty || "",
        license_number: newTreatment.doctor_info?.license_number || ""
      },
      
      treatment_info: {
        start_date: newTreatment.treatment_info.start_date,
        estimated_end_date: newTreatment.treatment_info.estimated_end_date || null,
        status: newTreatment.treatment_info.status || 'active',
        progress_percentage: 0
      },
      
      consent_status: {
        is_required: newTreatment.consent_status.is_required ?? true,
        is_signed: newTreatment.consent_status.is_signed ?? false,
        pending_signature: newTreatment.consent_status.pending_signature ?? true
      }
    };

    // 🔍 VALIDAR DATOS COMPLETOS
    console.log('🔍 Running complete data validation...');
    const validationErrors = validateTreatmentData(treatmentData);
    if (validationErrors.length > 0) {
      throw new Error('Data validation failed:\n' + validationErrors.join('\n'));
    }

    console.log('✅ All validations passed');
    console.log('🚀 Sending treatment data to server...');
    
    // 🚀 CREAR TRATAMIENTO
    const result = await createDetailTreatmentWithDoctor(treatmentData);
    
    console.log('🎉 Treatment created successfully!', result);
    
    // 🔄 RECARGAR DATOS
    console.log('🔄 Refreshing treatment lists...');
    await Promise.all([
      loadTreatments(),
      loadTreatmentsSummary()
    ]);
    
    // 🎉 CERRAR MODAL
    handleCloseOffcanvas();
    
    // 🎉 MOSTRAR MENSAJE DE ÉXITO
    alert('✅ Treatment created successfully!\n\n' + 
          `Treatment: ${result.data?.treatment_info?.name || 'New Treatment'}\n` +
          `Doctor: ${result.data?.doctor_info?.doctor_name || 'Assigned Doctor'}\n` +
          `Status: ${result.data?.treatment_info?.status || 'Pending'}`);
    
  } catch (error) {
    console.error('❌ Error creating treatment:', error);
    
    // 🚨 MANEJO ESPECÍFICO DE ERRORES
    let userFriendlyMessage = 'An unexpected error occurred while creating the treatment.';
    
    if (error.message.includes('Patient ID')) {
      userFriendlyMessage = '❌ Patient information is invalid. Please refresh the page and try again.';
    } else if (error.message.includes('Doctor ID')) {
      userFriendlyMessage = '❌ Selected doctor is invalid. Please choose a different doctor.';
    } else if (error.message.includes('Treatment Template ID')) {
      userFriendlyMessage = '❌ Selected treatment template is invalid. Please choose a different template.';
    } else if (error.message.includes('Validation failed')) {
      userFriendlyMessage = '❌ Form validation failed:\n' + error.message.replace('Data validation failed:', '');
    } else if (error.message.includes('already exists')) {
      userFriendlyMessage = '❌ This treatment already exists for this patient. Please check existing treatments.';
    } else if (error.message.includes('Network error')) {
      userFriendlyMessage = '❌ Unable to connect to the server. Please check your internet connection and try again.';
    } else if (error.message.includes('not found')) {
      userFriendlyMessage = '❌ One or more selected items (doctor/template/patient) could not be found. Please refresh and try again.';
    } else if (error.message.includes('Validation errors:')) {
      userFriendlyMessage = '❌ Server validation failed:\n' + error.message;
    } else {
      userFriendlyMessage = '❌ Server error: ' + error.message;
    }
    
    // Mostrar error al usuario
    alert(userFriendlyMessage + '\n\n' + 
          'Technical details have been logged to the console. ' +
          'If this problem persists, please contact technical support.');
    
  } finally {
    setIsSubmitting(false);
  }
}, [
  newTreatment, 
  patientId, 
  loadTreatments, 
  loadTreatmentsSummary, 
  handleCloseOffcanvas
]);

// 🔍 FUNCIÓN DE VALIDACIÓN EN TIEMPO REAL
const validateFormInRealTime = useCallback(() => {
  const errors = [];
  
  if (!newTreatment.doctor_id) {
    errors.push('Doctor selection is required');
  }
  
  if (!newTreatment.treatment_info.treatment_template_id) {
    errors.push('Treatment template selection is required');
  }
  
  if (!newTreatment.treatment_info.start_date) {
    errors.push('Start date is required');
  } else {
    const startDate = new Date(newTreatment.treatment_info.start_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (startDate < today) {
      errors.push('Start date cannot be in the past');
    }
  }
  
  return errors;
}, [newTreatment]);

  // 🔥 CAMBIO 11: Memoizar funciones de utilidad
  const getStatusColor = useCallback((status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'in_progress':
      case 'active':
        return 'warning';
      case 'pending':
        return 'secondary';
      case 'suspended':
        return 'danger';
      default:
        return 'primary';
    }
  }, []);

  const getStatusIcon = useCallback((status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return <FiCheck />;
      case 'in_progress':
      case 'active':
        return <FiClock />;
      case 'pending':
        return <FiPause />;
      case 'suspended':
        return <FiSquare />;
      default:
        return '•';
    }
  }, []);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return '-';
    }
  }, []);

  const formatDateTime = useCallback((dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '-';
    }
  }, []);

  // 🔥 CAMBIO 12: Memoizar funciones de filtros
  const getActiveFilters = useCallback(() => {
    const activeFilters = [];
    
    if (globalFilter) {
      activeFilters.push({ key: 'search', label: `Search: "${globalFilter}"` });
    }
    
    if (filters.status !== 'All') {
      activeFilters.push({ key: 'status', label: `Status: ${filters.status}` });
    }
    
    if (filters.consentStatus !== 'All') {
      activeFilters.push({ key: 'consentStatus', label: `Consent: ${filters.consentStatus}` });
    }
    
    if (filters.startDate) {
      activeFilters.push({ key: 'startDate', label: `From: ${new Date(filters.startDate).toLocaleDateString()}` });
    }
    
    if (filters.endDate) {
      activeFilters.push({ key: 'endDate', label: `To: ${new Date(filters.endDate).toLocaleDateString()}` });
    }
    
    if (filters.lastUpdate) {
      activeFilters.push({ key: 'lastUpdate', label: `Last Update: ${new Date(filters.lastUpdate).toLocaleDateString()}` });
    }
    
    return activeFilters;
  }, [globalFilter, filters]);

  const removeFilter = useCallback((filterKey) => {
    switch (filterKey) {
      case 'search':
        setGlobalFilter('');
        break;
      case 'status':
        handleFilterChange('status', 'All');
        break;
      case 'consentStatus':
        handleFilterChange('consentStatus', 'All');
        break;
      case 'startDate':
        handleFilterChange('startDate', '');
        break;
      case 'endDate':
        handleFilterChange('endDate', '');
        break;
      case 'lastUpdate':
        handleFilterChange('lastUpdate', '');
        break;
      default:
        break;
    }
  }, [handleFilterChange]);

  const getActiveFiltersCount = useCallback(() => {
    return getActiveFilters().length;
  }, [getActiveFilters]);

  const getSelectedTreatmentDetails = useCallback(() => {
    if (!newTreatment.treatment_info.treatment_template_id) return null;
    
    return availableTreatments.find(t => t.id === newTreatment.treatment_info.treatment_template_id);
  }, [newTreatment.treatment_info.treatment_template_id, availableTreatments]);

  const handleCloseViewOffcanvas = useCallback(() => {
    setShowViewOffcanvas(false);
    setSelectedTreatment(null);
  }, []);

  // 🔥 CAMBIO 13: Memoizar las columnas para evitar recreaciones con doctor info
  const columns = useMemo(() => [
    {
      accessorKey: 'treatment_info.name',
      header: 'Treatment Name',
      size: 200,
      Cell: ({ cell, row }) => (
        <div>
          <strong>{cell.getValue() || 'Unnamed Treatment'}</strong>
          {row.original.treatment_info.protocol_code && (
            <div className="text-muted small">
              Code: {row.original.treatment_info.protocol_code}
            </div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'doctor_info.doctor_name',
      header: 'Doctor',
      size: 150,
      Cell: ({ cell }) => (
        <div className="d-flex align-items-center gap-2">
          <FiUser className="text-muted" />
          <span>{cell.getValue() || 'Not assigned'}</span>
        </div>
      ),
    },
    {
      accessorKey: 'treatment_info.last_updated',
      header: 'Last Update',
      size: 120,
      Cell: ({ cell, row }) => {
        const lastUpdated = cell.getValue() || 
                           row.original.updated_date || 
                           row.original.treatment_info.start_date ||
                           row.original.created_date;
        return formatDate(lastUpdated);
      },
    },
    {
      accessorKey: 'treatment_info.status',
      header: 'Status',
      size: 120,
      Cell: ({ cell }) => {
        const status = cell.getValue();
        const color = getStatusColor(status);
        const icon = getStatusIcon(status);
        
        return (
          <span className={`badge bg-${color} d-flex align-items-center gap-1`}>
            {icon}
            {status ? status.replace('_', ' ').toUpperCase() : 'UNKNOWN'}
          </span>
        );
      },
    },
    {
      accessorKey: 'consent_status',
      header: 'Consent',
      size: 150,
      Cell: ({ cell, row }) => {
        const consent = cell.getValue();
        const treatmentId = row.original.treatment_id;
        
        if (!consent.is_required) {
          return <span className="badge bg-secondary">Not Required</span>;
        }
        
        if (consent.is_signed) {
          return (
            <span className="badge bg-success d-flex align-items-center gap-1">
              <FiCheck size={12} />
              Signed
            </span>
          );
        }
        
        return (
          <button
            className="btn btn-outline-warning btn-sm"
            onClick={() => handleConsentAction(treatmentId)}
            title="Click to mark as signed"
          >
            <FiAlertTriangle className="me-1" size={12} />
            Pending
          </button>
        );
      },
    },
    {
      accessorKey: 'treatment_info.start_date',
      header: 'Start Date',
      size: 120,
      Cell: ({ cell }) => formatDate(cell.getValue()),
    },
    {
      id: 'actions',
      header: 'Actions',
      size: 100,
      Cell: ({ row }) => (
        <div className="d-flex gap-1">
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={() => handleViewTreatment(row.original.treatment_id)}
            title="View details"
          >
            <FiEye size={12} />
          </button>
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => console.log('Edit treatment:', row.original.treatment_id)}
            title="Edit treatment"
          >
            <FiSettings size={12} />
          </button>
        </div>
      ),
    },
  ], [getStatusColor, getStatusIcon, formatDate, handleConsentAction, handleViewTreatment]);

  // Render component
  return (
    <div className="card mb-4">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h6 className="mb-0">Treatment Details</h6>
        <div className="d-flex align-items-center gap-2">
          {alertInfo && (
            <span className="badge bg-danger">{alertInfo.priority === 'high' ? '1' : '0'}</span>
          )}
        </div>
      </div>

      {/* Alert Banner - e-Consent Pending */}
      {alertInfo && (
        <div className="alert alert-warning d-flex justify-content-between align-items-center mb-0 border-0 rounded-0">
          <div className="d-flex align-items-center gap-2">
            <FiAlertTriangle className="text-warning" />
            <div>
              <strong>{alertInfo.alert_type === 'consent' ? 'e-Consent Pending' : 'Alert'}</strong>
              <div className="text-muted small">{alertInfo.message}</div>
            </div>
          </div>
          <div className="d-flex align-items-center gap-2">
            {alertInfo.alert_type === 'consent' && (
              <button className="btn btn-outline-warning btn-sm">
                View Document
              </button>
            )}
            <div className="bg-light rounded p-2">
              <span className="small text-muted">e-Consent Pending</span>
              <div className="text-primary"><FiFileText size={20} /></div>
            </div>
          </div>
        </div>
      )}

      <div className="card-body">
        {/* Enhanced Search and Actions Bar */}
        <div className="row mb-3">
          <div className="col-12">
            <div className="d-flex align-items-center gap-2">
              {/* Search Input */}
              <div className="input-group" style={{maxWidth: '300px'}}>
                <span className="input-group-text">
                  <FiSearch />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by name"
                  value={globalFilter}
                  onChange={(e) => handleGlobalFilterChange(e.target.value)}
                />
              </div>

              {/* Most Recent Dropdown */}
              <div className="dropdown" style={{position: 'relative'}}>
                <button
                  className="btn btn-outline-secondary d-flex align-items-center gap-2"
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowMostRecentDropdown(!showMostRecentDropdown);
                  }}
                >
                  Most Recent <FiChevronDown />
                </button>
                
                {showMostRecentDropdown && (
                  <div className="dropdown-menu show" style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    zIndex: 1000,
                    minWidth: '160px'
                  }}>
                    <button
                      className="dropdown-item d-flex align-items-center gap-2"
                      onClick={() => handleMostRecentAction('sendMail')}
                    >
                      <FiMail /> Send Mail
                    </button>
                    <button
                      className="dropdown-item d-flex align-items-center gap-2"
                      onClick={() => handleMostRecentAction('sendMessage')}
                    >
                      <FiMessageSquare /> Send Message
                    </button>
                    <button
                      className="dropdown-item d-flex align-items-center gap-2"
                      onClick={() => handleMostRecentAction('downloadReport')}
                    >
                      <FiDownload /> Download Report
                    </button>
                  </div>
                )}
              </div>

              {/* Filter Button */}
              <button
                className="btn btn-outline-secondary position-relative"
                onClick={() => setShowFilterOffcanvas(true)}
              >
                <FiFilter />
                {getActiveFiltersCount() > 0 && (
                  <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-primary">
                    {getActiveFiltersCount()}
                  </span>
                )}
              </button>

              {/* Settings Button */}
              <button className="btn btn-outline-secondary">
                <FiSettings />
              </button>

              
            </div>
          </div>
        </div>

        {/* Active Filters Display */}
        {getActiveFiltersCount() > 0 && (
          <div className="mb-3">
            <div className="d-flex flex-wrap gap-2 align-items-center">
              <span className="text-muted small">Active filters:</span>
              {getActiveFilters().map((filter) => (
                <span key={filter.key} className="badge bg-light text-dark d-flex align-items-center gap-1">
                  {filter.label}
                  <button
                    className="btn-close btn-close-sm"
                    style={{ fontSize: '0.6em' }}
                    onClick={() => removeFilter(filter.key)}
                  ></button>
                </span>
              ))}
              <button
                className="btn btn-link btn-sm text-muted p-0"
                onClick={handleClearFilters}
              >
                Clear all
              </button>
            </div>
          </div>
        )}

        {/* Table */}
        <MaterialReactTable
          columns={columns}
          data={filteredTreatments}
          state={{
            isLoading,
            rowSelection,
            globalFilter,
          }}
          onRowSelectionChange={setRowSelection}
          onGlobalFilterChange={handleGlobalFilterChange}
          enableRowSelection={false}
          enableColumnFilters={false}
          enableGlobalFilter={false}
          enableDensityToggle={false}
          enableFullScreenToggle={false}
          enableColumnActions={false}
          enableHiding={false}
          enableSorting={true}
          initialState={{
            density: 'compact',
            pagination: { pageSize: 10 },
          }}
          muiTableProps={{
            sx: {
              '& .MuiTableHead-root': {
                backgroundColor: '#f8f9fa',
              },
            },
          }}
          muiTableBodyRowProps={({ row }) => ({
            onClick: () => handleViewTreatment(row.original.treatment_id),
            sx: {
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: '#f8f9fa',
              },
            },
          })}
        />

        {/* No data message */}
        {!isLoading && filteredTreatments.length === 0 && (
          <div className="text-center py-4">
            <div className="text-muted">
              {treatments.length === 0 ? 'No treatments found' : 'No treatments match your filters'}
            </div>
          </div>
        )}
      </div>

      {/* Filter Offcanvas */}
      {showFilterOffcanvas && (
        <>
          <div className="offcanvas-backdrop fade show" onClick={() => setShowFilterOffcanvas(false)}></div>
          <div className="offcanvas offcanvas-end show" style={{ visibility: 'visible' }}>
            <div className="offcanvas-header">
              <h5 className="offcanvas-title">Filter Treatments</h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => setShowFilterOffcanvas(false)}
              ></button>
            </div>
            <div className="offcanvas-body">
              <form>
                {/* Status Filter */}
                <div className="mb-3">
                  <label className="form-label">Status</label>
                  <select
                    className="form-select"
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                  >
                    <option value="All">All Statuses</option>
                    <option value="Active">Active</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Completed">Completed</option>
                    <option value="Pending">Pending</option>
                    <option value="Suspended">Suspended</option>
                  </select>
                </div>

                {/* Consent Status Filter */}
                <div className="mb-3">
                  <label className="form-label">Consent Status</label>
                  <select
                    className="form-select"
                    value={filters.consentStatus}
                    onChange={(e) => handleFilterChange('consentStatus', e.target.value)}
                  >
                    <option value="All">All</option>
                    <option value="Signed">Signed</option>
                    <option value="Pending">Pending</option>
                    <option value="Not Required">Not Required</option>
                  </select>
                </div>

                {/* Start Date Filter */}
                <div className="mb-3">
                  <label className="form-label">Start Date From</label>
                  <input
                    type="date"
                    className="form-control"
                    value={filters.startDate}
                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  />
                </div>

                {/* End Date Filter */}
                <div className="mb-3">
                  <label className="form-label">Start Date To</label>
                  <input
                    type="date"
                    className="form-control"
                    value={filters.endDate}
                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  />
                </div>

                {/* Last Update Filter */}
                <div className="mb-3">
                  <label className="form-label">Last Update</label>
                  <input
                    type="date"
                    className="form-control"
                    value={filters.lastUpdate}
                    onChange={(e) => handleFilterChange('lastUpdate', e.target.value)}
                  />
                </div>
              </form>

              <div className="d-flex gap-2 mt-4">
                <button
                  className="btn btn-primary flex-grow-1"
                  onClick={handleApplyFilters}
                >
                  Apply Filters
                </button>
                <button
                  className="btn btn-outline-secondary"
                  onClick={handleClearFilters}
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Add New Treatment Offcanvas */}
      {showOffcanvas && (
        <>
          <div className="offcanvas-backdrop fade show" onClick={handleCloseOffcanvas} />
          <div className="offcanvas offcanvas-end show" tabIndex="-1" style={{ width: '600px' }}>
            <div className="offcanvas-header">
              <h5 className="offcanvas-title">Add New Treatment</h5>
              <button 
                type="button" 
                className="btn-close" 
                onClick={handleCloseOffcanvas}
              />
            </div>
            <div className="offcanvas-body">
              <form onSubmit={handleSubmitNewTreatment}>
                {/* Doctor Selection */}
                <div className="mb-3">
                  <label className="form-label">
                    <FiUser className="me-2" />
                    Assigned Doctor <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    value={newTreatment.doctor_id}
                    onChange={(e) => handleDoctorSelection(e.target.value)}
                    required
                    disabled={loadingDoctors}
                  >
                    <option value="">
                      {loadingDoctors ? 'Loading doctors...' : 'Select a doctor'}
                    </option>
                    {doctors.map((doctor) => (
                      <option key={doctor._id} value={doctor._id}>
                        {doctor.name} {doctor.surname}
                        {doctor.specialty && ` - ${doctor.specialty}`}
                      </option>
                    ))}
                  </select>
                  {newTreatment.doctor_id && newTreatment.doctor_info && (
                    <div className="form-text">
                      <small className="text-muted">
                        Selected: {newTreatment.doctor_info.doctor_name}
                        {newTreatment.doctor_info.specialty && ` (${newTreatment.doctor_info.specialty})`}
                      </small>
                    </div>
                  )}
                </div>

                {/* Treatment Selection */}
                <div className="mb-3">
                  <label className="form-label">
                    <FiFileText className="me-2" />
                    Treatment Template <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    value={newTreatment.treatment_info.treatment_template_id}
                    onChange={(e) => handleTreatmentSelection(e.target.value)}
                    required
                    disabled={loadingTreatments}
                  >
                    <option value="">
                      {loadingTreatments ? 'Loading treatments...' : 'Select a treatment template'}
                    </option>
                    {availableTreatments.map((treatment) => (
                      <option key={treatment.id} value={treatment.id}>
                        {treatment.name} ({treatment.protocol_code})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Treatment Details Preview */}
                {getSelectedTreatmentDetails() && (
                  <div className="mb-3">
                    <div className="card bg-light">
                      <div className="card-body">
                        <h6 className="card-title">{getSelectedTreatmentDetails().name}</h6>
                        <p className="card-text small">
                          <strong>Protocol:</strong> {getSelectedTreatmentDetails().protocol_code}<br/>
                          <strong>Duration:</strong> {getSelectedTreatmentDetails().estimated_duration_days} days<br/>
                          <strong>Specialty:</strong> {getSelectedTreatmentDetails().specialty_name}<br/>
                          <strong>Phases:</strong> {getSelectedTreatmentDetails().phases_count}
                        </p>
                        {getSelectedTreatmentDetails().description && (
                          <p className="card-text small">
                            <strong>Description:</strong> {getSelectedTreatmentDetails().description}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Start Date */}
                <div className="mb-3">
                  <label className="form-label">
                    <FiCalendar className="me-2" />
                    Start Date <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    value={newTreatment.treatment_info.start_date}
                    onChange={(e) => handleFormChange('start_date', e.target.value, 'treatment_info')}
                    required
                  />
                </div>

                {/* Estimated End Date */}
                <div className="mb-3">
                  <label className="form-label">
                    <FiCalendar className="me-2" />
                    Estimated End Date
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    value={newTreatment.treatment_info.estimated_end_date}
                    onChange={(e) => handleFormChange('estimated_end_date', e.target.value, 'treatment_info')}
                  />
                </div>

                {/* Status */}
                <div className="mb-3">
                  <label className="form-label">
                    <FiTag className="me-2" />
                    Status
                  </label>
                  <select
                    className="form-select"
                    value={newTreatment.treatment_info.status}
                    onChange={(e) => handleFormChange('status', e.target.value, 'treatment_info')}
                  >
                    <option value="active">Active</option>
                    <option value="pending">Pending</option>
                    <option value="suspended">Suspended</option>
                  </select>
                </div>

                {/* Consent Options */}
                <div className="mb-3">
                  <label className="form-label">
                    <FiLock className="me-2" />
                    Consent Requirements
                  </label>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={newTreatment.consent_status.is_required}
                      onChange={(e) => handleFormChange('is_required', e.target.checked, 'consent_status')}
                    />
                    <label className="form-check-label">
                      Consent required for this treatment
                    </label>
                  </div>
                  {newTreatment.consent_status.is_required && (
                    <div className="form-check mt-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={newTreatment.consent_status.is_signed}
                        onChange={(e) => {
                          handleFormChange('is_signed', e.target.checked, 'consent_status');
                          handleFormChange('pending_signature', !e.target.checked, 'consent_status');
                        }}
                      />
                      <label className="form-check-label">
                        Consent already signed
                      </label>
                    </div>
                  )}
                </div>

                <div className="d-flex gap-2">
                  <button
                    type="submit"
                    className="btn btn-primary flex-fill"
                    disabled={isSubmitting || !newTreatment.doctor_id || !newTreatment.treatment_info.treatment_template_id}
                  >
                    {isSubmitting ? 'Creating...' : 'Create Treatment'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary flex-fill"
                    onClick={handleCloseOffcanvas}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

      {/* View Treatment Offcanvas */}
      {showViewOffcanvas && selectedTreatment && (
        <>
          <div className="offcanvas-backdrop fade show" onClick={handleCloseViewOffcanvas}></div>
          <div className="offcanvas offcanvas-end show" style={{ visibility: 'visible', width: '600px' }}>
            <div className="offcanvas-header">
              <h5 className="offcanvas-title">Treatment Details</h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleCloseViewOffcanvas}
              ></button>
            </div>
            <div className="offcanvas-body">
              <div className="row">
                <div className="col-12">
                  {/* Treatment Header */}
                  <div className="d-flex justify-content-between align-items-start mb-4">
                    <div>
                      <h4>{selectedTreatment.treatment_info.name}</h4>
                      <p className="text-muted mb-0">
                        Protocol: {selectedTreatment.treatment_info.protocol_code}
                      </p>
                    </div>
                    <span className={`badge bg-${getStatusColor(selectedTreatment.treatment_info.status)} d-flex align-items-center gap-1`}>
                      {getStatusIcon(selectedTreatment.treatment_info.status)}
                      {selectedTreatment.treatment_info.status?.toUpperCase()}
                    </span>
                  </div>

                  {/* Treatment Information */}
                  <div className="card mb-3">
                    <div className="card-header">
                      <h6 className="mb-0 d-flex align-items-center gap-2">
                        <FiInfo /> Treatment Information
                      </h6>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-6">
                          <strong>Start Date:</strong><br />
                          {formatDate(selectedTreatment.treatment_info.start_date)}
                        </div>
                        <div className="col-6">
                          <strong>Estimated End Date:</strong><br />
                          {formatDate(selectedTreatment.treatment_info.estimated_end_date)}
                        </div>
                      </div>
                      <div className="row mt-3">
                        <div className="col-6">
                          <strong>Last Updated:</strong><br />
                          {formatDateTime(selectedTreatment.treatment_info.last_updated || selectedTreatment.updated_date)}
                        </div>
                        <div className="col-6">
                          <strong>Created:</strong><br />
                          {formatDateTime(selectedTreatment.created_date)}
                        </div>
                      </div>
                      {selectedTreatment.treatment_info.description && (
                        <div className="mt-3">
                          <strong>Description:</strong><br />
                          {selectedTreatment.treatment_info.description}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Consent Status */}
                  <div className="card mb-3">
                    <div className="card-header">
                      <h6 className="mb-0 d-flex align-items-center gap-2">
                        <FiLock /> Consent Status
                      </h6>
                    </div>
                    <div className="card-body">
                      {selectedTreatment.consent_status.is_required ? (
                        <div>
                          <div className="d-flex align-items-center gap-2 mb-2">
                            <span className={`badge ${selectedTreatment.consent_status.is_signed ? 'bg-success' : 'bg-warning'}`}>
                              {selectedTreatment.consent_status.is_signed ? 'Signed' : 'Pending'}
                            </span>
                            {selectedTreatment.consent_status.is_signed ? <FiCheck /> : <FiClock />}
                          </div>
                          {selectedTreatment.consent_status.signed_date && (
                            <p className="mb-0">
                              <strong>Signed Date:</strong> {formatDateTime(selectedTreatment.consent_status.signed_date)}
                            </p>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted">Consent not required for this treatment</span>
                      )}
                    </div>
                  </div>

                  {/* Active Alerts */}
                  {selectedTreatment.active_alerts && selectedTreatment.active_alerts.length > 0 && (
                    <div className="card mb-3">
                      <div className="card-header">
                        <h6 className="mb-0 d-flex align-items-center gap-2">
                          <FiAlertTriangle /> Active Alerts
                        </h6>
                      </div>
                      <div className="card-body">
                        {selectedTreatment.active_alerts.map((alert, index) => (
                          <div key={index} className={`alert alert-${alert.priority === 'high' ? 'danger' : 'warning'} mb-2`}>
                            <div className="d-flex align-items-center gap-2">
                              <FiAlertTriangle />
                              <div>
                                <strong>{alert.alert_type?.toUpperCase()}</strong>
                                <div>{alert.message}</div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Phases Summary */}
                  {selectedTreatment.phases_summary && selectedTreatment.phases_summary.length > 0 && (
                    <div className="card mb-3">
                      <div className="card-header">
                        <h6 className="mb-0 d-flex align-items-center gap-2">
                          <FiCalendar /> Treatment Phases
                        </h6>
                      </div>
                      <div className="card-body">
                        {selectedTreatment.phases_summary.map((phase, index) => (
                          <div key={index} className="border-bottom pb-2 mb-2 last:border-bottom-0 last:pb-0 last:mb-0">
                            <div className="d-flex justify-content-between align-items-center">
                              <strong>Phase {index + 1}: {phase.name || `Phase ${index + 1}`}</strong>
                              <span className={`badge bg-${getStatusColor(phase.status)}`}>
                                {phase.status?.toUpperCase() || 'PENDING'}
                              </span>
                            </div>
                            {phase.description && (
                              <p className="text-muted small mb-1">{phase.description}</p>
                            )}
                            {phase.duration_days && (
                              <p className="text-muted small mb-0">Duration: {phase.duration_days} days</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DetailsTreatments;